'use client'

import {Badge, Box, Container, Stack, Text} from '@blocks'
import {
   type FAQ,
   faq,
   siteFaqSectionBackgroundHex,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {FaqAccordion} from '@/blocks/site/FaqAccordion'

/** Desktop column width for FAQ accordion (stays same when items expand). */
const FAQ_DESKTOP_MAX_WIDTH_PX = 680

type Payload = Readonly<{
   categoryPriority: FAQ['category']
   bare?: boolean
}>

const formatCategory = (category: string) => {
   const withSpaces = category.replace(/-/g, ' ')
   return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

export const Faq = (payload: Payload) => {
   const isMobile = useMediaQuery('(max-width: 768px)')

   const categoryGroup = faq.find(
      (group) => group.category === payload.categoryPriority
   )

   if (!categoryGroup || categoryGroup.items.length === 0) {
      return null
   }

   if (payload.bare) {
      return (
         <Box
            w={'100%'}
            miw={0}
            maw={isMobile ? '100%' : FAQ_DESKTOP_MAX_WIDTH_PX}
            mx={isMobile ? 0 : 'auto'}
            style={{boxSizing: 'border-box'}}
         >
            <FaqAccordion
               categoryPriority={payload.categoryPriority}
               parentSurface={'dark'}
            />
         </Box>
      )
   }

   const columnMaxWidth = isMobile ? '100%' : FAQ_DESKTOP_MAX_WIDTH_PX

   return (
      <Stack bg={siteFaqSectionBackgroundHex}>
         <Container
            fluid
            py={isMobile ? 88 : 120}
            px={{base: 'md', sm: 'lg', md: 'xl'}}
         >
            <Box
               w={'100%'}
               miw={0}
               maw={columnMaxWidth}
               mx={'auto'}
               style={{boxSizing: 'border-box'}}
            >
               <Stack
                  align={'stretch'}
                  gap={isMobile ? 40 : 52}
               >
                  <Stack
                     align={'center'}
                     gap={16}
                  >
                     <Badge
                        size={'lg'}
                        fw={700}
                        tt={'uppercase'}
                        styles={{
                           root: {
                              border: 'none',
                              backgroundColor:
                                 'rgba(255,255,255,0.12)',
                              color: 'white'
                           }
                        }}
                     >
                        FAQ
                     </Badge>
                     <Text
                        c={'white'}
                        fz={
                           isMobile
                              ? siteTypographySectionTitleSecondaryFontSizeMobile
                              : siteTypographySectionTitleSecondaryFontSizeDesktop
                        }
                        fw={800}
                        ta={'center'}
                        lh={1.12}
                        w={'100%'}
                     >
                        {formatCategory(payload.categoryPriority)}{' '}
                        Questions
                     </Text>
                  </Stack>
                  <FaqAccordion
                     categoryPriority={payload.categoryPriority}
                     parentSurface={'dark'}
                  />
               </Stack>
            </Box>
         </Container>
      </Stack>
   )
}
